import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { useAuth } from '@/contexts/auth-context';

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    // If already signed in on hard reload, context isn't available here yet.
    // Client-side redirect handled in the component once auth state loads.
    return {};
  },
  component: LoginPage,
});

// Hero background — reuse the same asset the Overwolf desktop login uses so
// the two surfaces feel like the same product.
const HERO_IMAGE_URL =
  'https://mainstage-dev-publicbucketbucket-btrauwee.s3.amazonaws.com/assets/images/login2.webp';

function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Redirect in an effect instead of throwing during render — `throw redirect`
  // only works in a loader/beforeLoad. During render it just crashes into the
  // error boundary as an uncaught Response.
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      void navigate({ to: '/app', replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center p-6 sm:p-8 relative overflow-hidden">
      {/* Layered hero background — mirrors the Overwolf desktop login so the
          web and desktop surfaces feel like the same product. */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          src={HERO_IMAGE_URL}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover login-bg-zoom"
        />
        <div
          className="absolute inset-0 bg-gradient-to-br from-primary/30 via-primary/10 to-transparent animate-pulse"
          style={{ animationDuration: '3s' }}
        />
        <div
          className="absolute inset-0 bg-gradient-to-tl from-accent/20 via-transparent to-primary/20 animate-pulse"
          style={{ animationDuration: '4s', animationDelay: '1s' }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_20%,rgba(0,0,0,0.7)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/85 to-background/90" />
      </div>

      <div className="relative z-10 w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-5xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
            MAINSTAGE
          </h1>
          <p className="text-muted-foreground">Companion App</p>
        </div>

        <div className="bg-card backdrop-blur-2xl border border-border rounded-2xl p-8 shadow-2xl space-y-6 hover:shadow-primary/10 transition-shadow duration-500">
          <div className="space-y-2 text-center">
            <h2 className="text-2xl font-semibold">Welcome back</h2>
            <p className="text-sm text-muted-foreground">
              Sign in to access your stats and matchup guides
            </p>
          </div>

          <div className="flex justify-center">
            <GoogleSignInButton onError={setError} />
          </div>

          {error && (
            <div
              role="alert"
              className="text-sm text-destructive text-center p-3 bg-destructive/10 rounded-lg border border-destructive/20"
            >
              {error}
            </div>
          )}

          <div className="pt-4 border-t border-border/50">
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <div
                  className="h-2 w-2 rounded-full bg-primary/70 animate-pulse"
                  style={{ animationDuration: '2s' }}
                />
                <span>Track your performance</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <div
                  className="h-2 w-2 rounded-full bg-primary/70 animate-pulse"
                  style={{ animationDuration: '2.5s' }}
                />
                <span>Learn matchups</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <div
                  className="h-2 w-2 rounded-full bg-primary/70 animate-pulse"
                  style={{ animationDuration: '3s' }}
                />
                <span>Get live insights</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground/60 text-center pt-2">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="underline underline-offset-4 hover:text-foreground">Terms and Conditions</Link>{' '}
            and{' '}
            <Link to="/privacy" reloadDocument className="underline underline-offset-4 hover:text-foreground">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
